import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { CirclePlus } from "lucide-react";

import CustomInput from "../../../components/UI/PageInput/CustomInput";
import CustomDescriptionInput from "../../../components/UI/PageInput/CustomDescriptionInput";
import ComplaintDate from "../../../components/UI/PageInput/ComplaintDate";
import CenteredHeading from "../../../components/UI/headingsUI/CenteredHeading";
import GrayButton from "../../../components/UI/buttonUI/GrayButton";
import CustomFileInput from "../../../components/UI/PageInput/CustomFileInput";
import SelectedImagesBox from "../../../components/expense/add/SelectedImagesBox";
import SearchDropdown from "../../../components/UI/PageInput/SearchDropdown";
import ImageSlider from "../../../components/UI/viewDialogs/ImageSlider";
import ComplaintViewDialog from "../components/complaintDialog/ComplaintViewDialog";
import UseDialogBox from "../../../CustomHooks/UseDialogBox";
import { getCompressImage } from "../../../functions/getCompressImage";
import PrimaryButton from "../../../components/UI/buttonUI/PrimaryButton";
import {
  noSpaceOnlyValidation,
  phoneValidation,
} from "../../../validations/formValidations";

const AddComplaint = ({
  complaintSetup,
  loading,
  sendData,
  successResponse,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      pictures: [], // Initialize pictures as an empty array
    },
  });
  const { showDialogBox, setShowDialogBox } = UseDialogBox();
  const [imageSliderPics, setImageSliderPics] = useState([]);
  const [imageSliderCurrentIndex, setImageSliderCurrentIndex] = useState();
  const [openViewDialog, setViewDialog] = useState(false);

  const {
    pictures: pic,
    station_id,
    category_id,
    receiver_id,
    received_at,
    files,
  } = watch();

  const { append, remove } = useFieldArray({
    control,
    name: "files",
  });

  useEffect(() => {
    getCompressImage(pic, append);
  }, [pic]);

  const onSubmit = (data) => {
    sendData(data, reset);
  };

  const openImageSlider = () => {
    setViewDialog(null);
    setShowDialogBox(true);
    setImageSliderPics(successResponse.pictureList);
  };

  const closeImageSlider = () => {
    setViewDialog(true);
    setShowDialogBox(false);
  };

  // show success dialog on api response
  useEffect(() => {
    if (successResponse) {
      setViewDialog(true);
    }
  }, [successResponse]);

  return (
    <div className="form-container">
      <CenteredHeading title="Add Complaint" />
      <form
        className="2xs:max-lg:pt-0"
        onSubmit={!loading ? (e) => handleSubmit(onSubmit)(e) : undefined}
      >
        <div className="flex flex-wrap">
          <div className="inputLeftCol">
            <div className="sm:flex">
              <div className="w-full sm:pr-1">
                <SearchDropdown
                  name="station_id"
                  label="Site Name *"
                  keyName="name"
                  id="id"
                  options={complaintSetup.stations}
                  setValue={setValue}
                  selectedVal={station_id}
                  control={control}
                  error={errors.station_id}
                  search={true}
                />
              </div>
              <div className="w-full sm:pl-1">
                <SearchDropdown
                  name="category_id"
                  label="category *"
                  keyName="name"
                  id="id"
                  options={complaintSetup.categories}
                  setValue={setValue}
                  selectedVal={category_id}
                  control={control}
                  error={errors.category_id}
                />
              </div>
            </div>
            <div className="sm:flex">
              <div className="w-full sm:pr-1">
                <SearchDropdown
                  name="receiver_id"
                  label="Received By *"
                  keyName="fullName"
                  id="id"
                  options={complaintSetup.users}
                  setValue={setValue}
                  selectedVal={receiver_id}
                  control={control}
                  error={errors.receiver_id}
                  search={true}
                />
              </div>
              <div className="w-full sm:pl-1">
                <ComplaintDate
                  label="Date*"
                  name={"received_at"}
                  setValue={setValue}
                  value={received_at}
                  register={{
                    ...register("received_at", {
                      required: "Please Select date",
                    }),
                  }}
                  error={errors.received_at}
                />
              </div>
            </div>
            <CustomDescriptionInput
              name="description"
              labelText="Description *"
              register={{
                ...register("description", {
                  required: "Please Enter Description",
                  ...noSpaceOnlyValidation,
                }),
              }}
              error={errors.description}
            />
          </div>
          <div className="inputRightCol">
            <CustomFileInput
              name="pictures"
              placeholder="Add Image (jpg, png)"
              accept=".png, .jpg, image/png, image/jpeg"
              register={{
                ...register("pictures"),
              }}
              multiple={true}
            />
            <SelectedImagesBox
              errors={errors.pictures}
              selectedImages={files}
              setValue={setValue}
              name="pictures"
              remove={remove}
            />
          </div>
        </div>
        <div className="flex flex-wrap 2xs:max-md:pt-2">
          <div className="inputLeftCol">
            <CustomInput
              type="text"
              name="actual_fault"
              labelText="Actual Fault"
              placeholder="Enter"
              register={{
                ...register("actual_fault", {
                  ...noSpaceOnlyValidation,
                }),
              }}
              error={errors.actual_fault}
            />
          </div>
          <div className="inputRightCol">
            <div className="sm:flex">
              <div className="w-full sm:pr-1">
                <CustomInput
                  type="text"
                  name="office_remark"
                  labelText="Office Remark"
                  placeholder="Enter"
                  register={{
                    ...register("office_remark", {
                      ...noSpaceOnlyValidation,
                    }),
                  }}
                  error={errors.office_remark}
                />
              </div>
              <div className="w-full sm:pl-1">
                <CustomInput
                  type="number"
                  name="complainant_phone_number"
                  labelText="Phone No."
                  placeholder="Enter"
                  register={{
                    ...register("complainant_phone_number", {
                      ...phoneValidation,
                    }),
                  }}
                  error={errors.complainant_phone_number}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="complaintBtnWrap">
          <p className="pr-2">
            <PrimaryButton
              btnText={!loading ? "Add Complaint" : "Adding..."}
              loading={loading}
              Icon={CirclePlus}
              size={20}
            />
          </p>
          <p className="pl-2">
            <GrayButton btnText="Clear" width="w-40" onClick={() => reset()} />
          </p>
        </div>
      </form>
      {openViewDialog && (
        <ComplaintViewDialog
          viewDetails={successResponse}
          openImageSlider={openImageSlider}
          setImageSliderCurrentIndex={setImageSliderCurrentIndex}
          successView={true}
        />
      )}
      {showDialogBox && (
        <ImageSlider
          title="Expense Photos"
          images={imageSliderPics}
          imageSliderCurrentIndex={imageSliderCurrentIndex}
          closeDialogBox={closeImageSlider}
        />
      )}
    </div>
  );
};
export default AddComplaint;
